"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { uploadFile } from "@/actions/upload";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { LoadingSpinner } from "@/components/ui/spinner";
import FilePreview from "@/components/utils/preview";

export default function PDFCompressor() {
  const [selectedFile, setSelectedFile] = useState<File>();
  const [compressedPdf, setCompressedPdf] = useState<Blob | null>(null);

  const [processState, setProcessState] = useState<"ready" | "processing">(
    "ready"
  );

  const { toast } = useToast();

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleCompress = async () => {
    if (selectedFile) {
      setProcessState("processing");
      try {
        const compressData = await uploadFile(selectedFile);
        if (compressData.file instanceof Blob) {
          setCompressedPdf(compressData.file);
        }
      } catch (e) {
        toast({
          variant: "destructive",
          title: "Uh oh! Something went wrong.",
          description: "There was a problem with your request.",
          action: <ToastAction altText="Try again">Try again</ToastAction>,
        });
      } finally {
        setProcessState("ready");
      }
    }
  };

  const handleDownloadFile = () => {
    if (compressedPdf) {
      const downloadLink = URL.createObjectURL(compressedPdf);
      const a = document.createElement("a");
      a.href = downloadLink;
      a.download = "compressed_file.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      toast({
        title: "Uh oh! Failed to download.",
        description: "Sorry not file to download",
        action: <ToastAction altText="Try again">Try again</ToastAction>,
      });
    }
  };

  return (
    <main className="flex flex-col items-center justify-between gap-2">
      <label
        htmlFor="fileInput"
        className="mt-4 p-4 bg-blue-500 text-white cursor-pointer rounded-lg"
      >
        Select PDF File{" "}
      </label>
      <input
        type="file"
        id="fileInput"
        accept=".pdf"
        className="hidden"
        onChange={handleFileChange}
      />

      {selectedFile && (
        <div className="flex flex-col items-center gap-2">
          <span>{selectedFile.name}</span>
          <Button
            variant={"link"}
            onClick={() => setSelectedFile(undefined)}
            disabled={processState != "ready"}
          >
            Clear
          </Button>
          <p>Selected File Size: {(selectedFile.size / 1024).toFixed(1)} KB</p>
        </div>
      )}

      {selectedFile && (
        <>
          <FilePreview file={selectedFile} />
          <div>
            {processState === "processing" ? (
              <div className="flex gap-2">
                <LoadingSpinner /> Compressing...
              </div>
            ) : (
              <Button
                onClick={handleCompress}
                disabled={processState != "ready"}
              >
                Compress
              </Button>
            )}
          </div>
        </>
      )}

      {compressedPdf && (
        <div className="flex flex-col gap-2 items-center">
          <p className="mt-2">
            Compressed File Size: {(compressedPdf.size / 1024).toFixed(1)} KB
          </p>
          <div>
            <Button
              variant={"secondary"}
              onClick={handleDownloadFile}
              disabled={processState != "ready"}
            >
              Download
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}
